//src/pages/SongEditPage.tsx
import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { MainSongForm } from '../components/songEdit/forms/MainSongForm';
import { SongSearchAutocomplete } from '../components/songEdit/search/SongSearchAutocomplete';
import { VersionsList } from '../components/songEdit/versions/VersionsList';
import { PendingVersionsList } from '../components/songEdit/versions/PendingVersionsList';
import { useSongEditState } from '../components/songEdit/hooks/useSongEditState';
import { useSongEditHandlers } from '../components/songEdit/hooks/useSongEditHandlers';
import ConfirmationModal from '../components/songEdit/modals/ConfirmationModal';
import { AddFileAfterSaveModal } from '../components/songEdit/modals/AddFileAfterSaveModal';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { songEditService } from '../services/songEditService';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import { SONG_COLLECTIONS } from '../types/songCollections';
import '../components/songEdit/styles/songEdit.scss';

interface NewSongInfo {
  song_id: number;
  title: string;
  version_id: number;
}

interface SongEditPageProps {
  hideNavigation?: boolean;
  initialSongId?: number;
  startNew?: boolean;
  initialCollection?: string;
}

const SongEditPage: React.FC<SongEditPageProps> = ({ hideNavigation = false, initialSongId, startNew = false, initialCollection }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [entityExistsError, setEntityExistsError] = useState<any>(null);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [newSongInfo, setNewSongInfo] = useState<NewSongInfo | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    showSongForm,
    setShowSongForm,
    formData,
    setFormData,
    initialFormData,  // Make sure this is included from useSongEditState
    isDirty,
    resetForms,
    loadSongData,
    handleFormChange,
    handleSaveSuccess,
    // Pending version management
    addPendingVersion,
    updatePendingVersion,
    removePendingVersion,
    setPendingVersionDefault
  } = useSongEditState();

  const {
    handleSongSelect,
    handleSave,
  } = useSongEditHandlers({
    setFormData,
    setShowSongForm,
    resetForms,
    loadSongData,
    handleSaveSuccess,
    isDirty,
    initialFormData  // Pass it here
  });

  // Auto-load song if initialSongId is provided (e.g., from URL query param)
  useEffect(() => {
    if (initialSongId && !initialLoadDone) {
      setInitialLoadDone(true);
      handleSongSelect(initialSongId);
    }
  }, [initialSongId, initialLoadDone, handleSongSelect]);

  useEffect(() => {
    if (startNew && !initialSongId && !initialLoadDone) {
      const collection = SONG_COLLECTIONS.find(item => item.id === initialCollection);
      setInitialLoadDone(true);
      setFormData(previous => ({ ...previous, tags: collection ? [collection.tag] : [] }));
      setShowSongForm(true);
    }
  }, [startNew, initialSongId, initialLoadDone, initialCollection, setFormData, setShowSongForm]);

  // Prevent accidental navigation when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleNewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    resetForms();
    setShowSongForm(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmModal(false);
    const isNewSong = !formData.song_id;

    try {
      const result = await handleSave(formData);

      // If a new song was created, fetch the default version and show add file modal
      if (isNewSong && result?.song_id) {
        try {
          // Fetch versions to get the default version ID
          const versionsResponse = await apiClient.get(`/songs/${result.song_id}/versions`);
          const versions = versionsResponse.data.data || [];
          const defaultVersion = versions.find((v: any) => v.is_default) || versions[0];

          if (defaultVersion) {
            setNewSongInfo({
              song_id: result.song_id,
              title: result.title,
              version_id: defaultVersion.version_id
            });
            setShowAddFileModal(true);
          } else {
            // No versions found, just close the form
            handleSaveSuccess();
          }
        } catch (err) {
          console.error('[SongEditPage] Error fetching versions:', err);
          // If fetching versions fails, just close the form
          handleSaveSuccess();
        }
      }
    } catch (error: any) {
      // Check if this is an ENTITY_EXISTS error
      if (error.code === 'ENTITY_EXISTS') {
        setEntityExistsError(error);
      }
    }
  };

  const handleAddFileModalClose = () => {
    setShowAddFileModal(false);
    setNewSongInfo(null);
    handleSaveSuccess();
  };

  const handleSaveClick = () => {
    console.log('[SongEditPage] Opening confirmation modal with data:', formData);
    setShowConfirmModal(true);
  };

  const handleModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleUseExistingEntity = async () => {
    if (!entityExistsError) return;

    const { entityType, existingEntity } = entityExistsError;

    // Update formData with the existing entity ID
    const updatedFormData = { ...formData };

    if (entityType === 'composer') {
      updatedFormData.composerData = {
        ...updatedFormData.composerData,
        composer_id: existingEntity.composer_id,
        full_name: existingEntity.full_name,
        era: existingEntity.era,
        origin: existingEntity.origin
      };
    } else if (entityType === 'lyricist') {
      updatedFormData.lyricistData = {
        ...updatedFormData.lyricistData,
        composer_id: existingEntity.composer_id,
        full_name: existingEntity.full_name,
        era: existingEntity.era,
        origin: existingEntity.origin
      };
    } else if (entityType === 'tune') {
      updatedFormData.primaryTune = existingEntity.tune_name;
      updatedFormData.tune_id = existingEntity.tune_id;
      updatedFormData.tuneData = {
        tune_id: existingEntity.tune_id,
        tune_name: existingEntity.tune_name,
        meter_id: existingEntity.meter_id
      };
    }

    setFormData(updatedFormData);
    setEntityExistsError(null);

    // Retry save with updated data
    try {
      await handleSave(updatedFormData);
    } catch (error: any) {
      if (error.code === 'ENTITY_EXISTS') {
        // Another entity conflict, show dialog again
        setEntityExistsError(error);
      }
    }
  };

  const handleEditEntityName = () => {
    // Close dialog, keep modal open for user to edit
    setEntityExistsError(null);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!formData.song_id) return;

    setIsDeleting(true);
    try {
      await songEditService.deleteSong(formData.song_id);
      setShowDeleteModal(false);
      resetForms();
      setShowSongForm(false);
    } catch (error) {
      console.error('Error deleting song:', error);
      alert('Failed to delete song. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {!hideNavigation && <Navigation />}
      <div className="song-management container mt-4">
      <div className="song-management__header d-flex justify-content-between align-items-center">
        <h2 className="mb-0">Song Management</h2>
        <button 
          className="song-management__new-button btn"
          onClick={handleNewClick}
        >
          <Plus size={24} />
        </button>
      </div>

      <SongSearchAutocomplete onSongSelect={handleSongSelect} />

      {showSongForm && (
        <div className="song-form mt-4">
          <MainSongForm
            formData={formData}
            onChange={handleFormChange}
          />

          {/* Display versions list - different component for new vs existing songs */}
          {formData.song_id ? (
            // Existing song: show versions from database
            <VersionsList songId={formData.song_id} />
          ) : (
            // New song: show pending versions (to be created with song)
            <PendingVersionsList
              pendingVersions={formData.pendingVersions || []}
              onAdd={addPendingVersion}
              onUpdate={updatePendingVersion}
              onRemove={removePendingVersion}
              onSetDefault={setPendingVersionDefault}
            />
          )}

          <div className="song-form__actions d-flex justify-content-between mt-4">
            {formData.song_id ? (
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleDeleteClick}
              >
                <Trash2 size={18} className="me-1" />
                Delete Song
              </button>
            ) : (
              <div></div>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveClick}
            >
              Save Song
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleModalClose}
        onConfirm={handleConfirmSave}
        data={formData}
      />

      {/* Entity Exists Dialog */}
      {entityExistsError && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {entityExistsError.entityType.charAt(0).toUpperCase() + entityExistsError.entityType.slice(1)} Already Exists
                </h5>
              </div>
              <div className="modal-body">
                <p>{entityExistsError.message}</p>
                {entityExistsError.existingEntity && (
                  <div className="alert alert-info">
                    <strong>Existing {entityExistsError.entityType}:</strong><br/>
                    {entityExistsError.existingEntity.full_name || entityExistsError.existingEntity.tune_name}
                    {entityExistsError.existingEntity.era && ` (${entityExistsError.existingEntity.era})`}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleEditEntityName}
                >
                  No, Edit Name
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUseExistingEntity}
                >
                  Yes, Use Existing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add File After Save Modal */}
      {newSongInfo && (
        <AddFileAfterSaveModal
          isOpen={showAddFileModal}
          onClose={handleAddFileModalClose}
          songId={newSongInfo.song_id}
          songTitle={newSongInfo.title}
          versionId={newSongInfo.version_id}
        />
      )}

      {/* Delete Song Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Song"
        message={`Are you sure you want to delete "${formData.title}"? This will also delete all versions, files, and service selections for this song. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
    </>
  );
};

export default SongEditPage;